import type { ReactNode } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';
import { FeedbackMenu } from './FeedbackMenu';
import HeaderClientPicker from './HeaderClientPicker';
import AccountMenu from './AccountMenu';
import { useMyAccess, useUserPreferences } from '../../services/conversionsApi';
import { withClientId } from '../../lib/clientContext';
import MomentHost from '../moments/MomentHost';
import MomentsToggle from '../moments/MomentsToggle';
import {
  HomeRegular,
  PeopleRegular,
  TableRegular,
  BookRegular,
  ArrowExportRegular,
  ArrowSwapRegular,
  MegaphoneRegular,
  DataBarVerticalRegular,
} from '@fluentui/react-icons';

interface Props {
  children: ReactNode;
}

export default function AppLayout({ children }: Props) {
  const { t } = useTranslation();
  // The nav carries the current client to every client-scoped screen (docs/client-context.md): the URL's, or on a
  // client-free screen the remembered one. Only clientId travels.
  const [searchParams] = useSearchParams();
  const prefs = useUserPreferences();
  const navClientId = searchParams.get('clientId') || prefs.data?.lastClientId || '';

  // Only core's Admins see the DEV section (fails closed: no answer, no section).
  const access = useMyAccess();

  // In the product's order, ruled (Christian, 2026-10-28): journals from sources first, then bank statement → Xero
  // file, then everything else. `Export` is long gone: it carried uploads, both bank flows and the manual journal in one
  // column and asked for the client twice.
  const navItems = [
    { to: '/', icon: <HomeRegular />, label: t('nav.landing'), end: true },
    { to: '/clients', icon: <PeopleRegular />, label: t('nav.clients'), end: false },
    { to: '/journals', icon: <BookRegular />, label: t('nav.journals'), end: false },
    { to: '/bank-transactions', icon: <ArrowExportRegular />, label: t('nav.bankTransactions'), end: false },
    { to: '/gl-mappings', icon: <TableRegular />, label: t('nav.glMappings'), end: false },
    { to: '/extraction-accuracy', icon: <DataBarVerticalRegular />, label: t('nav.extractionAccuracy'), end: false },
    { to: '/whats-new', icon: <MegaphoneRegular />, label: t('nav.patchNotes'), end: false },
  ];
  // DEV, low priority (Christian's walk of deploy #20): Account transactions waits here, hidden from everyone but
  // core's Admins, until it has a real use (clearing reports, later).
  const devItems = [
    { to: '/account-transactions', icon: <ArrowSwapRegular />, label: t('nav.accountTransactions'), end: false },
  ];
  const navLink = (item: (typeof navItems)[number]) => (
    <NavLink
      key={item.to}
      to={withClientId(item.to, navClientId)}
      end={item.end}
      className={({ isActive }) => (isActive ? 'active' : '')}
    >
      {item.icon}
      {item.label}
    </NavLink>
  );

  // MomentHost wraps the whole shell so any page can fire a moment (docs/accomplishment-moments.md).
  return (
    <MomentHost>
      <div className="app-container">
        <header className="app-header">
          <div className="app-header-brand">
            {/* i18n-ignore: the PTC brand mark is a logo, the same in every language (and aria-hidden) */}
            <span className="app-header-mark" aria-hidden="true">PTC</span>
            <h1>{t('brand')}</h1>
          </div>
          {/* The client search sits LEFT of center and takes the room (Christian's walk, 2026-10-28): it was squeezed
              in with the actions on the right, too narrow to read the picked client in full. */}
          <HeaderClientPicker />
          {/* Beside the language toggle, so it is on every staff page (fleet feedback contract v1 §1). */}
          <div className="app-header-actions">
            <FeedbackMenu />
            <MomentsToggle />
            <LanguageToggle />
            <AccountMenu />
          </div>
        </header>
        <div className="app-body">
          <nav className="app-nav">
            {navItems.map(navLink)}
            {access.data?.isAdmin && (
              <div className="app-nav-section" role="group" aria-labelledby="app-nav-dev">
                <div className="app-nav-section-label" id="app-nav-dev">{t('nav.devSection')}</div>
                {devItems.map(navLink)}
              </div>
            )}
          </nav>
          <main className="app-content">{children}</main>
        </div>
      </div>
    </MomentHost>
  );
}
